export interface ColorPalette {
  id: string;
  name: string;
  baseHue: number;
  baseSaturation: number;

  light: {
    base: string;
    elevated: string;
    surface: string;
    tile: string;
    tileHover: string;
    tileBorder: string;
    text: string;
    textMuted: string;
    textSubtle: string;
    border: string;
    borderMuted: string;
    accent: string;
    accentMuted: string;
    accentHover: string;
    accentText: string;
    toggleActive: string;
    toggleInactive: string;
    toggleActiveText: string;
    toggleInactiveText: string;
  };

  dark: {
    base: string;
    elevated: string;
    surface: string;
    tile: string;
    tileHover: string;
    tileBorder: string;
    text: string;
    textMuted: string;
    textSubtle: string;
    border: string;
    borderMuted: string;
    accent: string;
    accentMuted: string;
    accentHover: string;
    accentText: string;
    toggleActive: string;
    toggleInactive: string;
    toggleActiveText: string;
    toggleInactiveText: string;
  };
}

function hslToHex(h: number, s: number, l: number): string {
  h = h % 360;
  s = Math.max(0, Math.min(100, s)) / 100;
  l = Math.max(0, Math.min(100, l)) / 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;

  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }

  const toHex = (n: number) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  hex = hex.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) * 60; break;
      case g: h = ((b - r) / d + 2) * 60; break;
      case b: h = ((r - g) / d + 4) * 60; break;
    }
  }

  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function generatePalette(id: string, name: string, baseHex: string): ColorPalette {
  const { h, s } = hexToHsl(baseHex);

  const lightSat = Math.min(s, 40);
  const darkSat = Math.min(s + 10, 50);

  return {
    id,
    name,
    baseHue: h,
    baseSaturation: s,

    light: {
      base: hslToHex(h, lightSat * 0.15, 98),
      elevated: hslToHex(h, lightSat * 0.2, 100),
      surface: hslToHex(h, lightSat * 0.25, 96),
      tile: hslToHex(h, lightSat * 0.3, 97),
      tileHover: hslToHex(h, lightSat * 0.5, 94),
      tileBorder: hslToHex(h, lightSat * 0.4, 88),
      text: hslToHex(h, lightSat * 0.3, 15),
      textMuted: hslToHex(h, lightSat * 0.2, 40),
      textSubtle: hslToHex(h, lightSat * 0.15, 55),
      border: hslToHex(h, lightSat * 0.3, 85),
      borderMuted: hslToHex(h, lightSat * 0.2, 90),
      accent: hslToHex(h, s, 50),
      accentMuted: hslToHex(h, s * 0.6, 92),
      accentHover: hslToHex(h, s, 42),
      accentText: hslToHex(0, 0, 100),
      toggleActive: hslToHex(h, s * 0.8, 52),
      toggleInactive: hslToHex(h, lightSat * 0.3, 94),
      toggleActiveText: hslToHex(0, 0, 100),
      toggleInactiveText: hslToHex(h, lightSat * 0.3, 35),
    },

    dark: {
      // ChatBox AI Match - "Warm Stone" (Stone/Warm Gray Tones)
      base: '#1c1917',     // Stone 950/900 Mix - Deep warm background
      elevated: '#292524', // Stone 800 - Warm panel
      surface: '#44403c',  // Stone 700 - Visible warm surface

      tile: '#292524',
      tileHover: '#44403c',
      tileBorder: '#57534e', // Stone 600 - Warm border

      text: '#fafaf9',       // Stone 50 (Warm White)
      textMuted: '#a8a29e',  // Stone 400
      textSubtle: '#78716c', // Stone 500

      border: '#57534e',     // Stone 600
      borderMuted: '#44403c',

      accent: hslToHex(h, s * 0.9, 60),
      accentMuted: hslToHex(h, s * 0.4, 25),
      accentHover: hslToHex(h, s, 65),
      accentText: '#ffffff',

      toggleActive: hslToHex(h, s * 0.8, 55),
      toggleInactive: '#44403c',
      toggleActiveText: '#ffffff',
      toggleInactiveText: '#a8a29e',
    }
  };
}

export const COLOR_PRESETS: { id: string; name: string; hex: string }[] = [
  { id: 'ocean', name: 'Ocean Blue', hex: '#3B82F6' },
  { id: 'teal', name: 'Teal', hex: '#14B8A6' },
  { id: 'emerald', name: 'Emerald', hex: '#10B981' },
  { id: 'forest', name: 'Forest', hex: '#22C55E' },
  { id: 'violet', name: 'Violet', hex: '#8B5CF6' },
  { id: 'purple', name: 'Purple', hex: '#A855F7' },
  { id: 'fuchsia', name: 'Fuchsia', hex: '#D946EF' },
  { id: 'rose', name: 'Rose', hex: '#F43F5E' },
  { id: 'coral', name: 'Coral', hex: '#FB7185' },
  { id: 'amber', name: 'Amber', hex: '#F59E0B' },
  { id: 'orange', name: 'Orange', hex: '#F97316' },
  { id: 'slate', name: 'Slate', hex: '#64748B' },
  { id: 'zinc', name: 'Zinc', hex: '#71717A' },
  { id: 'indigo', name: 'Indigo', hex: '#6366F1' },
  { id: 'sky', name: 'Sky', hex: '#0EA5E9' },
  { id: 'cyan', name: 'Cyan', hex: '#06B6D4' },
];

export const DEFAULT_PALETTE = generatePalette('ocean', 'Ocean Blue', '#3B82F6');

export function getPaletteById(id: string): ColorPalette {
  const preset = COLOR_PRESETS.find(p => p.id === id);
  if (preset) {
    return generatePalette(preset.id, preset.name, preset.hex);
  }
  return DEFAULT_PALETTE;
}

export function applyPalette(palette: ColorPalette, isDark: boolean): void {
  const root = document.documentElement;
  const colors = isDark ? palette.dark : palette.light;

  root.style.setProperty('--cc-base', colors.base);
  root.style.setProperty('--cc-elevated', colors.elevated);
  root.style.setProperty('--cc-surface', colors.surface);
  root.style.setProperty('--cc-tile', colors.tile);
  root.style.setProperty('--cc-tile-hover', colors.tileHover);
  root.style.setProperty('--cc-tile-border', colors.tileBorder);
  root.style.setProperty('--cc-text', colors.text);
  root.style.setProperty('--cc-text-muted', colors.textMuted);
  root.style.setProperty('--cc-text-subtle', colors.textSubtle);
  root.style.setProperty('--cc-border', colors.border);
  root.style.setProperty('--cc-border-muted', colors.borderMuted);
  root.style.setProperty('--cc-accent', colors.accent);
  root.style.setProperty('--cc-accent-muted', colors.accentMuted);
  root.style.setProperty('--cc-accent-hover', colors.accentHover);
  root.style.setProperty('--cc-accent-text', colors.accentText);
  root.style.setProperty('--cc-toggle-active', colors.toggleActive);
  root.style.setProperty('--cc-toggle-inactive', colors.toggleInactive);
  root.style.setProperty('--cc-toggle-active-text', colors.toggleActiveText);
  root.style.setProperty('--cc-toggle-inactive-text', colors.toggleInactiveText);
}
